import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Typography, Button, Space, Tag, Divider, message, Spin, theme } from 'antd';
import { 
  LeftOutlined, 
  ClockCircleOutlined, 
  BookOutlined, 
  FileTextOutlined, 
  QuestionCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import api from '../../utils/api';
import styles from './WrongQuestionDetail.module.css';

const { Title, Text } = Typography;

const overrideStyles = {
  relatedQuestionsColumn: {
    width: '300px !important',
    minWidth: '300px !important',
    flex: '0 0 300px !important'
  }
};

const WrongQuestionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(location.state?.questionData || null);
  const { token } = theme.useToken();
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const relatedQuestionsRef = useRef(null);

  const fetchQuestionDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/study/wrong-questions/${id}`);
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        setQuestion(response.data.data);
      } else {
        throw new Error(response.data.message || '获取题目详情失败');
      }
    } catch (error) {
      console.error('获取错题详情失败:', error);
      message.error('获取题目详情失败');
      navigate('/wrong-questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedQuestions = async () => {
    try {
      // 确保参数存在
      if (!question?.questionInfo?.questionId || !question?.examInfo?.collection) {
        console.log('Missing required params:', {
          questionId: question?.questionInfo?.questionId,
          collection: question?.examInfo?.collection
        });
        return;
      }

      const params = {
        currentId: question.questionInfo.questionId,
        collection: question.examInfo.collection
      };

      console.log('Requesting related questions:', params);

      const response = await api.get('/study/wrong-questions/related', { params });
      console.log('Related questions response:', response.data);

      if (response.data.success) {
        // 检查调试信息
        if (response.data.debug) {
          console.log('Debug info:', response.data.debug);
        }
        setRelatedQuestions(response.data.data || []);
      }
    } catch (error) {
      console.error('Related questions error:', {
        message: error.message,
        config: error.config,
        response: error.response?.data
      });
    }
  };

  useEffect(() => {
    if (!question && id) {
      fetchQuestionDetail();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    console.log('Question data updated:', {
      hasQuestion: !!question,
      questionId: question?.questionInfo?.questionId,
      collection: question?.examInfo?.collection
    });

    if (question?.questionInfo?.questionId && question?.examInfo?.collection) {
      console.log('Triggering related questions fetch');
      fetchRelatedQuestions();
    }
  }, [question]);

  useEffect(() => {
    if (relatedQuestionsRef.current) {
      relatedQuestionsRef.current.style.width = '300px';
      relatedQuestionsRef.current.style.minWidth = '300px';
      relatedQuestionsRef.current.style.flex = '0 0 300px';
    }
  });

  useEffect(() => {
    const targetNode = document.querySelector('div[style*="width: 300px"]');
    if (!targetNode) return;
    
    const observer = new MutationObserver(() => {
      targetNode.style.width = '300px';
      targetNode.style.minWidth = '300px';
      targetNode.style.flex = '0 0 300px';
    });
    
    observer.observe(targetNode, { 
      attributes: true, 
      attributeFilter: ['style'] 
    });
    
    // 立即应用样式
    targetNode.style.width = '300px';
    targetNode.style.minWidth = '300px';
    targetNode.style.flex = '0 0 300px';
    
    return () => observer.disconnect();
  }, []);

  const handleBack = () => {
    navigate('/wrong-questions');
  };

  // 添加一个简单的样式修复函数
  const fixRelatedColumnWidth = () => {
    const relatedColumn = document.querySelector('div[style*="display: flex"] > div:last-child');
    if (relatedColumn) {
      relatedColumn.style.width = '300px';
      relatedColumn.style.minWidth = '300px';
      relatedColumn.style.flex = '0 0 300px';
    }
  };
  
  // 在组件挂载后和每次重新渲染后应用样式修复
  useEffect(() => {
    // 立即执行一次
    fixRelatedColumnWidth();
    
    // 然后设置一个定时器，在页面完全加载后再次执行
    const timer = setTimeout(fixRelatedColumnWidth, 500);
    
    // 清理函数
    return () => clearTimeout(timer);
  });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (!question) {
    return (
      <div className={styles.errorContainer}>
        <Card className={styles.card}>
          <Title level={4} style={{ color: '#fff' }}>题目不存在或已被删除</Title>
          <Button type="primary" onClick={handleBack}>返回错题列表</Button>
        </Card>
      </div>
    );
  }

  // 处理选项数据
  const options = Array.isArray(question.questionInfo?.options) 
    ? question.questionInfo.options.map((option, index) => ({
        label: String.fromCharCode(65 + index),
        text: typeof option === 'object' ? option.label || option.text : option
      }))
    : [];

  // 获取正确答案和用户答案
  const correctAnswer = question.questionInfo?.answer?.[0] || '';
  const userAnswer = question.questionInfo?.userAnswer?.[0] || '';

  return (
    <div className={styles.container}>
      <div className={styles.contentLayout}>
        {/* 主要内容区域 */}
        <div className={styles.mainContent}>
          <Card className={styles.card} bordered={false}>
            <div className={styles.headerContainer}>
              <Title level={3} className={styles.pageTitle}>错题详情</Title>
              <Button onClick={handleBack}>返回错题本</Button>
            </div>

            {/* 分类信息 */}
            <div className={styles.section}>
              <Space size={24}>
                <span>{question.examInfo?.examData || '-'}</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.45)' }}>{question.examInfo?.category || '-'}</span>
              </Space>
            </div>

            {/* 题目内容 */}
            <div className={styles.section}>
              <Title level={4} className={styles.sectionTitle}>题目</Title>
              <div style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{question.questionInfo?.questionText}</div>
            </div>

            {/* 选项 */}
            {question.questionInfo?.options && (
              <div className={styles.section}>
                <Title level={4} className={styles.sectionTitle}>选项</Title>
                <div className={styles.optionsContainer}>
                  {question.questionInfo.options.map((option, index) => (
                    <div key={index} style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                      {option.label}. {option.text}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 答案信息 */}
            <div className={styles.section}>
              <Title level={4} className={styles.sectionTitle}>答案</Title>
              <div className={styles.answerContainer}>
                <div className={styles.answerLabel}>正确答案</div>
                <div className={styles.correctAnswer}>
                  <CheckCircleOutlined className={styles.correctAnswerIcon} />
                  <div className={styles.correctAnswerContent}>
                    {Array.isArray(question.questionInfo?.answer) ? 
                      question.questionInfo.answer.join('、') : 
                      question.questionInfo?.answer || '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* 解析 */}
            {question.questionInfo?.explanation && (
              <div className={styles.section}>
                <Title level={4} className={styles.sectionTitle}>解析</Title>
                <div style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{question.questionInfo.explanation}</div>
              </div>
            )}
          </Card>
        </div>

        {/* 右侧相关题目 */}
        <div className={styles.sideContent} ref={relatedQuestionsRef}>
          <Card 
            bordered={false}
            className={styles.relatedQuestionCard}
            title={<div className={styles.relatedQuestionTitle}>相关题目</div>}
          >
            <div className={styles.relatedQuestionList}>
              {relatedQuestions.length > 0 ? (
                relatedQuestions.map((item) => (
                  <div 
                    key={item._id}
                    className={styles.relatedQuestionItem}
                    onClick={() => {
                      // 使用 window.open 在新窗口打开
                      window.open(`/questions/${question.examInfo.collection}/${item._id}`, '_blank');
                    }}
                  >
                    <div className={styles.relatedQuestionExam}>
                      {item.exam_data || '-'}
                    </div>
                    <div className={styles.relatedQuestionText}>
                      {item.question || item.questionText}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ color: 'rgba(255, 255, 255, 0.45)', textAlign: 'center' }}>
                  暂无相关题目
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WrongQuestionDetail; 